import { Box } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext } from 'react';

import { RicosContext } from './../../ricos-context/RicosContext';
import { useBiNext } from './../../_shared/bi-context-provider/bi-context-provider';
import { useNodeAttrs } from './../../_shared/use-node-attrs';
import { HTML_BUTTONS } from './../consts';
import { Panel, PanelHeader, SliderWithInput, ToggleButton } from './../../ui-components';

import {
  HTML_DIMENSIONS_INPUT,
  HTML_DIMENSIONS_SLIDER,
  HTML_HEIGHT_AUTOHEIGHT_TOGGLE,
  HTML_SLIDER_MODAL,
} from '../data-hooks';
import { INIT_HEIGHT, INIT_WIDTH } from '../defaults';

export type SliderModalProps = {
  onChange: ({ value, autoHeight }: { value: number; autoHeight?: boolean }) => void;
  closeModal: () => void;
  min: number;
  max: number;
  dimension: 'width' | 'height';
  nodeId: string;
};

export const SliderModal: FC<SliderModalProps> = ({
  onChange,
  closeModal,
  min,
  max,
  dimension,
  nodeId,
}) => {
  const { pmNodeAttrs } = useNodeAttrs(nodeId, 'HTML');
  const logBi = useBiNext({ pluginId: 'html', modalId: HTML_BUTTONS[dimension], nodeId });

  if (!pmNodeAttrs) {
    return null;
  }
  const value =
    parseInt(pmNodeAttrs.containerData?.[dimension]?.custom || '') ||
    (dimension === 'height' ? INIT_HEIGHT : INIT_WIDTH);

  const naturalIframeHeight = pmNodeAttrs.iframeHeight;

  const autoHeight = pmNodeAttrs.autoHeight ?? true;

  const { t } = useContext(RicosContext);

  return (
    <Panel onCloseButtonClick={closeModal} height="100%" dataHook={HTML_SLIDER_MODAL}>
      <PanelHeader
        title={t(
          dimension === 'height'
            ? 'HTMLPlugin_SliderModal_Title_Height'
            : 'HTMLPlugin_SliderModal_Title_Width'
        )}
      />
      <Box padding="18px" direction="vertical">
        {dimension === 'height' && pmNodeAttrs.html ? (
          <ToggleButton
            title={t('HTMLPlugin_SliderModal_Toggle_AutoHeight')}
            dataHook={HTML_HEIGHT_AUTOHEIGHT_TOGGLE}
            size="medium"
            checked={autoHeight}
            onChange={() => {
              logBi({ dataHook: HTML_HEIGHT_AUTOHEIGHT_TOGGLE, value: !autoHeight });
              onChange({ value, autoHeight: !autoHeight });
            }}
          />
        ) : null}
        <Box paddingTop="9px">
          <SliderWithInput
            logBi={logBi}
            min={min}
            max={max}
            label={null}
            sliderDataHook={HTML_DIMENSIONS_SLIDER}
            inputDataHook={HTML_DIMENSIONS_INPUT}
            onChange={value => {
              onChange({ value, autoHeight });
            }}
            value={
              dimension === 'height' && autoHeight && naturalIframeHeight
                ? naturalIframeHeight
                : value
            }
            disabled={dimension === 'height' && autoHeight && !!pmNodeAttrs.html}
            normalizeMaxValue={false}
          />
        </Box>
      </Box>
    </Panel>
  );
};
